import { useState } from "react";
import type { MetaFunction } from "react-router";
import { useFetcher, useLoaderData } from "react-router";
import { useTranslation } from "react-i18next";
import { Button, PageAssetsProvider } from "~/components/ui";
import { HomeHero } from "~/components/landing/HomeHero";
import { HomeMirror } from "~/components/landing/HomeMirror";
import { PhaseExplorer } from "~/components/landing/PhaseExplorer";
import { SensoryBand } from "~/components/landing/SensoryBand";
import { FounderSection } from "~/components/landing/FounderSection";
import { ProductCapture } from "~/components/landing/ProductCapture";
import { BenefitList, type Benefit } from "~/components/landing/BenefitList";
import { TestimonialList, type Testimonial } from "~/components/landing/TestimonialList";
import { ConversionSection } from "~/components/landing/ConversionSection";
import { WAITLIST_ANCHOR, WaitlistPanel, type WaitlistErrors } from "~/components/landing/WaitlistForm";
import { usePublicHomeSearch } from "~/components/layout/use-public-home";
import { buildLocalizedPath } from "~/config/route-config";
import { buildRouteMeta, localeFromPath } from "~/lib/seo";
import type { Language } from "~/lib/types";
import type { action, loader } from "./($lang)._index.loader.server";

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  if (!data) return [];
  // T134 — canonical + hreflang + Open Graph/Twitter via the shared helper.
  // The BO-managed `og-default` asset (T219/T256) overrides the static brand
  // card when present; `undefined` falls back to the static default.
  return buildRouteMeta({
    routeKey: "home",
    locale: localeFromPath(location.pathname),
    title: data.meta.title,
    description: data.meta.description,
    image: data.ogImageUrl ?? undefined,
  });
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A locale array read with `returnObjects`; anything else (a missing key) is an empty list. */
function list<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

/**
 * T872 S3 — the public Home of the October delivery (`components/home.page.html`): hero →
 * the method (intro + `PhaseExplorer`) → the sensory band → Gala → the practice on a dark
 * band (capture, three benefits, the invitation to the waitlist) → the students' words →
 * the closing (self-test first, the waitlist panel beside it). Header and footer come from
 * the root layout (S1); the shell's `darkHero` handle gives the bar its at-top dark
 * treatment over the hero.
 *
 * Form machine: idle → (client validation) → sending → success; invalid fields return to
 * idle with field errors + focus on the first one; a server failure shows the one alert
 * line and keeps the draft. A field error, client or server, clears when she edits that
 * field. Receipt: `tests/component-dom/872-home-waitlist-machine.test.tsx`.
 */
export default function HomeView() {
  const { pageAssets } = useLoaderData<typeof loader>();
  const { t, i18n } = useTranslation("home");
  // A bare `#…` drops `?public=1`, and the loader then sends a member to her space (N2).
  const publicHome = usePublicHomeSearch() ?? "";
  const lang = (i18n.language ?? "es") as Language;
  const autoTestHref = buildLocalizedPath("autoTest", lang);
  const fetcher = useFetcher<typeof action>();
  const [clientErrors, setClientErrors] = useState<{ email?: boolean; consent?: boolean }>({});
  // A server field error stays until she edits that field: `fetcher.data` outlives her
  // next keystroke, so the dismissal is tracked here and reset by the next submit.
  const [dismissed, setDismissed] = useState<{ email?: boolean; consent?: boolean }>({});

  const sending = fetcher.state !== "idle";
  const result = fetcher.data;
  const succeeded = result?.ok === true;
  const serverErrors = result && !result.ok ? result.errors : {};

  const errors: WaitlistErrors = {
    email: clientErrors.email || (serverErrors.email && !dismissed.email),
    consent: clientErrors.consent || (serverErrors.consent && !dismissed.consent),
    generic: serverErrors.generic,
  };
  const status = sending ? "sending" : errors.generic ? "error" : "idle";

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    const form = event.currentTarget;
    const data = new FormData(form);
    const email = String(data.get("email") ?? "").trim();
    const consent = data.get("consent") === "on";
    const next = { email: !EMAIL_RE.test(email), consent: !consent };
    if (next.email || next.consent) {
      event.preventDefault();
      setClientErrors(next);
      const firstInvalid = next.email ? "email" : "consent";
      (form.elements.namedItem(firstInvalid) as HTMLInputElement | null)?.focus();
      return;
    }
    setClientErrors({});
    setDismissed({});
  }

  function handleFieldChange(field: "email" | "consent") {
    setClientErrors((prev) => (prev[field] ? { ...prev, [field]: false } : prev));
    setDismissed((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
  }

  const benefits = list<Benefit>(t("experience.benefits", { returnObjects: true }));
  const stories = list<Testimonial>(t("stories.items", { returnObjects: true }));

  return (
    <PageAssetsProvider assets={pageAssets}>
      <div className="ho-page">
        <HomeHero />
        <HomeMirror />

        <section className="ho-method" id="metodo" aria-labelledby="ho-method-title">
          <div className="pub-wrap">
            <div className="ho-intro">
              <div>
                <p className="pub-eyebrow">{t("method.eyebrow")}</p>
                <h2 id="ho-method-title">
                  {t("method.title_lead")}
                  <br />
                  <em>{t("method.title_accent")}</em>
                </h2>
              </div>
              <p className="ho-intro-copy">{t("method.intro")}</p>
            </div>
            <PhaseExplorer />
          </div>
        </section>

        <SensoryBand />

        <FounderSection
          eyebrow={t("founder.eyebrow")}
          titleLead={t("founder.title_lead")}
          titleAccent={t("founder.title_accent")}
          quote={t("founder.quote")}
          body={t("founder.body")}
          signature={t("founder.signature")}
        />

        <section className="ho-experience" id="experiencia" aria-labelledby="ho-experience-title">
          <div className="pub-wrap">
            <div className="ho-experience-head">
              <div>
                <p className="pub-eyebrow">{t("experience.eyebrow")}</p>
                <h2 id="ho-experience-title">
                  {t("experience.title_lead")}
                  <br />
                  {t("experience.title_rest")} <em>{t("experience.title_accent")}</em>
                </h2>
              </div>
              <p className="ho-experience-lede">{t("experience.lede")}</p>
            </div>
            <ProductCapture caption={t("experience.capture_caption")} note={t("experience.capture_note")} />
            <BenefitList items={benefits} />
            <div className="ho-experience-invite">
              <p>{t("experience.invite")}</p>
              <Button variant="public-secondary" className="on-dark" href={`${publicHome}#${WAITLIST_ANCHOR}`}>
                {t("experience.invite_cta")}
              </Button>
            </div>
          </div>
        </section>

        <section className="ho-stories" id="opiniones" aria-labelledby="ho-stories-title">
          <div className="pub-wrap">
            <div className="ho-stories-head">
              <div>
                <p className="pub-eyebrow">{t("stories.eyebrow")}</p>
                <h2 id="ho-stories-title">
                  {t("stories.title_lead")} <em>{t("stories.title_accent")}</em>
                </h2>
              </div>
              <p className="ho-stories-intro">{t("stories.intro")}</p>
            </div>
            <TestimonialList items={stories} linkLabel={t("stories.link")} externalLabel={t("stories.external")} />
            <p className="ho-stories-note">{t("stories.note")}</p>
          </div>
        </section>

        <ConversionSection
          eyebrow={t("closing.eyebrow")}
          titleLead={t("closing.title_lead")}
          titleAccent={t("closing.title_accent")}
          body={t("closing.body")}
          ctaLabel={t("closing.cta")}
          ctaHref={autoTestHref}
          meta={t("closing.meta")}
          faqLabel={t("closing.faq_link")}
        >
          <WaitlistPanel
            Form={fetcher.Form}
            status={status}
            errors={errors}
            succeeded={succeeded}
            email={succeeded ? result.email : ""}
            onSubmit={handleSubmit}
            onFieldChange={handleFieldChange}
          />
        </ConversionSection>
      </div>
    </PageAssetsProvider>
  );
}
